import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BdcBranch, BdcDashboardSummary } from "../../../../types/bdcDashboard.type";
import { baht, pcs } from "../../format";
import { CARD_SX, TABLE_SX } from "../../styles";
import Tile, { TileGrid } from "../Tile";
import {
  branchLabel,
  changeText,
  percentText,
  periodLabel,
  previousLabel,
  seriesTitle,
  shareOf,
  shortfall,
} from "../format";
import SeriesChart, { SeriesLegend } from "./SeriesChart";

const SECTION_SX = { ...CARD_SX, p: 2, display: "flex", flexDirection: "column", gap: 1.25 } as const;
const NOTE_SX = { fontSize: "0.75rem", color: "text.secondary" } as const;

/** `▼ 38.5% จากศุกร์ก่อน (67,114)`; no previous figure → say so plainly. */
export const changeLine = (pct: number | null, previousText: string, previousValue: string) => {
  const change = changeText(pct);
  return change ? `${change} จาก${previousText} (${previousValue})` : `ไม่มียอด${previousText}ให้เทียบ`;
};

/**
 * Board 1 — ยอดสั่ง / ยอดส่ง for the selected period against the previous one. Every number
 * is per-store (kind 'A') POs; POs without a store list are summed apart in the note below
 * the DC table so they never skew the comparison.
 */
export default function SummaryView({
  summary,
  branches,
  onOpenStores,
}: Readonly<{ summary: BdcDashboardSummary; branches: BdcBranch[]; onOpenStores: () => void }>) {
  const { current, previous, change_pct: change, granularity } = summary;
  const prevText = previousLabel(granularity, summary.date);
  const showBranch = branches.length > 1;
  const branchOf = (companyId: string) =>
    branchLabel(branches.find((b) => b.company_id === companyId), companyId);
  const storesLabel =
    granularity === "day" ? "ร้านที่สั่งวันนี้" : granularity === "week" ? "ร้านที่สั่งในสัปดาห์" : "ร้านที่สั่งในเดือน";
  const noStore = current.no_store_po;
  const noStorePrev = previous.no_store_po;

  return (
    <Stack gap={1.75} sx={TABLE_SX}>
      <TileGrid columns={4}>
        <Tile
          label="ยอดสั่ง (PO)"
          value={pcs(current.ordered_qty)}
          unit="ชิ้น"
          sub={`฿${baht(current.ordered_amount)} ก่อน VAT`}
          foot={changeLine(change.ordered_qty, prevText, pcs(previous.ordered_qty))}
        />
        <Tile
          label="ยอดส่ง (DO)"
          value={pcs(current.delivered_qty)}
          unit="ชิ้น"
          sub={
            current.fill_rate === null ? "ยังไม่มียอดสั่ง" : `ส่งได้ ${percentText(current.fill_rate)} ของยอดสั่ง`
          }
          foot={`ส่งขาด ${pcs(shortfall(current.ordered_qty, current.delivered_qty))} ชิ้น`}
        />
        <Tile
          label="CN (คืน / ส่งไม่ครบ)"
          value="—"
          muted
          sub="ยังไม่รวมในแดชบอร์ด · ดูยอด CN ที่หน้าใบลดหนี้"
          foot="ยอดสุทธิ = ส่ง − CN"
        />
        <Tile
          label={storesLabel}
          value={pcs(current.stores_ordered)}
          sub={`${percentText(shareOf(current.stores_ordered, current.base_stores))} ของฐาน ${pcs(current.base_stores)} ร้าน`}
          foot={
            <Link component="button" type="button" onClick={onOpenStores} sx={{ fontSize: "inherit", color: "inherit" }}>
              หลุดรอบการสั่ง {pcs(current.lapsed_count)} ร้าน →
            </Link>
          }
        />
      </TileGrid>

      <Card sx={SECTION_SX}>
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography fontWeight={600}>{seriesTitle(summary.series, granularity)}</Typography>
          <SeriesLegend />
        </Stack>
        <SeriesChart series={summary.series} granularity={granularity} />
        <Typography sx={NOTE_SX}>
          ชี้ที่แท่งเพื่อดูยอดสั่ง / ยอดส่ง / ฿ ของ{granularity === "day" ? "วัน" : granularity === "week" ? "สัปดาห์" : "เดือน"}นั้น
          · ยอดส่งเท่ายอดสั่งจนกว่าจะมีการปรับยอดส่ง
        </Typography>
      </Card>

      <Card sx={SECTION_SX}>
        <Typography fontWeight={600}>
          แยก DC · {periodLabel(granularity, summary.period.from, summary.period.to)} เทียบ{prevText}
        </Typography>
        {summary.dcs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography className="sub">ไม่มียอดสั่งในช่วงนี้</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table className="bdc-t">
              <thead>
                <tr>
                  <th>DC</th>
                  {showBranch && <th>สาขา</th>}
                  <th className="n">ชิ้นสั่ง</th>
                  <th className="n">ชิ้นส่ง</th>
                  <th className="n">ส่งได้</th>
                  <th className="n">฿ ก่อน VAT</th>
                  <th className="n">{prevText}</th>
                  <th className="n">เปลี่ยน</th>
                </tr>
              </thead>
              <tbody>
                {summary.dcs.map((row) => (
                  <tr key={`${row.company_id}/${row.dc_code}`}>
                    <td>
                      <span className="dc">{row.dc_code}</span> <span className="sub">{row.dc_name}</span>
                    </td>
                    {showBranch && <td>{branchOf(row.company_id)}</td>}
                    <td className="n">{pcs(row.ordered_qty)}</td>
                    <td className="n">{pcs(row.delivered_qty)}</td>
                    <td className="n">{percentText(row.fill_rate)}</td>
                    <td className="n">{baht(row.ordered_amount)}</td>
                    <td className="n">{pcs(row.previous_ordered_qty)}</td>
                    <td className="n">{changeText(row.change_pct) ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={showBranch ? 2 : 1}>รวม {summary.dcs.length} DC</td>
                  <td className="n">{pcs(current.ordered_qty)}</td>
                  <td className="n">{pcs(current.delivered_qty)}</td>
                  <td className="n">{percentText(current.fill_rate)}</td>
                  <td className="n">{baht(current.ordered_amount)}</td>
                  <td className="n">{pcs(previous.ordered_qty)}</td>
                  <td className="n">{changeText(change.ordered_qty) ?? "—"}</td>
                </tr>
              </tfoot>
            </table>
          </Box>
        )}
        {(noStore.po_count > 0 || noStorePrev.po_count > 0) && (
          <Typography sx={NOTE_SX} data-testid="no-store-note">
            ไม่รวม PO แบบไม่มีรายร้าน: ช่วงนี้ {pcs(noStore.po_count)} ใบ · {pcs(noStore.qty)} ชิ้น · ฿
            {baht(noStore.amount)} — {prevText} {pcs(noStorePrev.po_count)} ใบ · {pcs(noStorePrev.qty)} ชิ้น
          </Typography>
        )}
      </Card>
    </Stack>
  );
}
